import { useMemo } from 'react';
import { Card, H2, Pill, CChart } from '../ui.jsx';
import { C, fmt, fmtK, pct, incK, CJSTT, mkScale } from '../lib/format.js';
import { INC, INC_LABEL, DAS } from '../lib/finance.js';

export default function PostRetire({ s, income, setIncome, deathAge, setDA, retYr, retSnap, postRet, L, d }) {
  const { isNarrow, g2, ch } = L;
  const { jSPY, oSPY, jSPN, oSPN } = d;

  const prId = useMemo(() => Date.now(), [postRet]);

  const prBarCfg = useMemo(() => ({
    type: 'bar',
    data: {
      labels: postRet.map(r => r.year),
      datasets: [
        { label: 'Pension draw', data: postRet.map(r => +r.pen.toFixed(1)), backgroundColor: C.purp + 'DD', stack: 's' },
        { label: 'Savings draw (ISA + PBs)', data: postRet.map(r => +r.sav.toFixed(1)), backgroundColor: C.gold + 'DD', stack: 's' },
        { label: 'James State Pension', data: postRet.map(r => +r.jSP.toFixed(1)), backgroundColor: C.blue + 'CC', stack: 's' },
        { label: 'Joanne State Pension', data: postRet.map(r => +r.oSP.toFixed(1)), backgroundColor: C.pink + 'CC', stack: 's' },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { tooltip: { ...CJSTT }, legend: { labels: { color: C.text, boxWidth: 12 } } },
      scales: { ...mkScale(), y: { ...mkScale().y, stacked: true } },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [prId]);

  const portCfg = useMemo(() => ({
    type: 'line',
    data: {
      labels: postRet.map(r => r.year),
      datasets: [
        { label: 'Pension pot', data: postRet.map(r => Math.round(r.pensionPot)), borderColor: C.purp, backgroundColor: C.purp + '18', fill: true, tension: .3, borderWidth: 2.5, pointRadius: 0 },
        { label: 'Savings (ISA + PBs)', data: postRet.map(r => Math.round(r.savingsPot)), borderColor: C.gold, backgroundColor: C.gold + '18', fill: true, tension: .3, borderWidth: 2.5, pointRadius: 0 },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { tooltip: { ...CJSTT }, legend: { labels: { color: C.text, boxWidth: 12 } } },
      scales: mkScale(),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [prId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: g2, gap: 12 }}>
        <Card>
          <div style={{ fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Income target</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {INC.map(t => <Pill key={t} label={incK(t) + (INC_LABEL[t] ? ' ' + INC_LABEL[t] : '')} active={income === t} onClick={() => setIncome(t)} />)}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Planning to Joanne's age</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {DAS.map(da => <Pill key={da} label={da} active={deathAge === da} onClick={() => setDA(da)} color={C.teal} />)}
          </div>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3,1fr)', gap: 12 }}>
        {[
          ["James's SP", fmt(jSPN) + '/yr', `From July ${jSPY}`, C.blue],
          ["Joanne's SP", fmt(oSPN) + '/yr', `From June ${oSPY}`, C.pink],
          [`Combined from ${oSPY}`, fmt(jSPN + oSPN) + '/yr', 'Covers most of £36k', C.green],
        ].map(([l, v, sub, c]) => (
          <div key={l} style={{ background: C.card, border: `1px solid ${C.bdr}`, borderRadius: 12, padding: '16px 16px', borderLeft: `3px solid ${c}` }}>
            <div style={{ fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>{l}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: c, fontFamily: "'DM Mono',monospace" }}>{v}</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>{sub}</div>
          </div>
        ))}
      </div>

      <Card>
        <H2 sub={`Nominal £k · Retire ${retYr < 2049 ? retYr : '2049+'} · Target ${incK(income)}/yr${INC_LABEL[income] ? ' (' + INC_LABEL[income] + ')' : ''} · Portfolio: ${retSnap ? fmtK(retSnap.total) : '—'}`}>
          Post-Retirement Income Sources
        </H2>
        <CChart cfg={prBarCfg} height={ch(340)} id={prId} />
        <p style={{ fontSize: 11, color: C.dim, marginTop: 10, lineHeight: 1.6 }}>
          Each year's spending is met in the tax-sensible order below. State pension steps in at {jSPY} and {oSPY}, replacing the bulk of the private draw.
        </p>
      </Card>

      <Card>
        <H2 sub="What's left in each pot after drawdown · £000s · pension drawn slowly early, savings carry the bridge years">Pots Remaining</H2>
        <CChart cfg={portCfg} height={ch(220)} id={prId + 'r'} />
      </Card>

      <Card>
        <H2 sub="Money drawn and what's left in the coffers at the end of each year (retirement portfolio; excludes the separate cash ISA reserve)">
          Year-by-Year Balances
        </H2>
        <div className="scroll-x scroll-y" style={{ maxHeight: '60vh' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 640 }}>
            <thead>
              <tr>
                {['Year', 'Ages J/O', 'Pension draw', 'Savings draw', 'State pension', 'Pension left', 'Savings left', 'Total left'].map((h, i) => (
                  <th key={h} style={{
                    padding: '10px 9px', textAlign: i < 2 ? 'left' : 'right', color: i === 7 ? C.gold : C.lbl,
                    fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap',
                    position: 'sticky', top: 0, background: C.card, borderBottom: `2px solid ${C.bdr}`, zIndex: 1,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {postRet.map((r, idx) => {
                const tot = r.pensionPot + r.savingsPot;
                const newDec = (r.year % 5 === 0);
                return (
                  <tr key={r.year} style={{
                    borderBottom: `1px solid ${C.bdr}`,
                    borderTop: newDec ? `2px solid ${C.steel}44` : 'none',
                    background: idx % 2 === 0 ? C.surf + '55' : 'transparent',
                  }}>
                    <td style={{ padding: '8px 9px', fontFamily: "'DM Mono',monospace", fontWeight: 600, color: C.bright }}>{r.year}</td>
                    <td style={{ padding: '8px 9px', fontFamily: "'DM Mono',monospace", color: C.dim }}>{r.year - 1972}/{r.year - 1974}</td>
                    <td style={{ padding: '8px 9px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.purp }}>£{Math.round(r.pen)}k</td>
                    <td style={{ padding: '8px 9px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.gold }}>£{Math.round(r.sav)}k</td>
                    <td style={{ padding: '8px 9px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.blue }}>£{Math.round(r.jSP + r.oSP)}k</td>
                    <td style={{ padding: '8px 9px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.text }}>£{Math.round(r.pensionPot)}k</td>
                    <td style={{ padding: '8px 9px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.text }}>£{Math.round(r.savingsPot)}k</td>
                    <td style={{ padding: '8px 9px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.gold, fontWeight: 700 }}>£{Math.round(tot)}k</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 11, color: C.dim, marginTop: 10, lineHeight: 1.6 }}>
          "Total left" is the combined pension + savings pot remaining at year end. Draws are gross. Figures are nominal £000s
          and assume {pct(s.drawdownGrowthPct)} growth on the remaining pots during drawdown.
        </p>
      </Card>

      <Card>
        <H2>Sensible Drawdown Approach</H2>
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
          <p style={{ marginBottom: 12 }}>
            The model fills spending each year in this order, which keeps tax low while running the pots down (you've set no
            estate target, so the aim is to use the money, not preserve it):
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              ['1', 'Pension up to the personal allowance', `Each of you draws taxable pension up to your unused £${(s.personalAllowance || 12570).toLocaleString('en-GB')} allowance — 0% tax. Before state pension age that's roughly £${(2 * (s.personalAllowance || 12570)).toLocaleString('en-GB')}/yr between you, completely tax-free.`, C.purp],
              ['2', 'Top up from tax-free savings', `Premium Bonds first (lowest growth at ${pct(s.pbPrizeRatePct)}, so spend these down first), then S&S ISAs. All tax-free, no allowance used.`, C.gold],
              ['3', 'Company reserve, early', `Wind up the company in the first year or two and take the ~${fmt(s.company * 0.9)} as dividends, ideally split across two tax years to stay inside the basic-rate dividend band.`, C.teal],
              ['4', 'Extra pension only if needed', `If savings run low, draw more pension — taxed at 20%. Keep each person's total taxable income under £50,270 to avoid 40% tax.`, C.blue],
            ].map(([n, t, desc, col]) => (
              <div key={n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: col + '22', color: col,
                  fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{n}</div>
                <div>
                  <div style={{ fontWeight: 600, color: C.bright, fontSize: 13 }}>{t}</div>
                  <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.6, marginTop: 2 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 14, fontSize: 11, color: C.dim, lineHeight: 1.6 }}>
            Why this order: the personal allowance is "use it or lose it" each year, so filling it with pension income is free
            money. Beyond that, tax-free ISA/Premium Bond withdrawals beat taxed pension withdrawals, so savings carry the
            bridge years until state pension arrives. Once both state pensions are flowing they cover most of the target, and
            the remaining gap comes mostly from tax-free savings. One side-effect worth knowing: because tax-free savings are
            spent ahead of taxed pension, the pensions are left largely intact and keep growing — at lower spending levels you
            may finish with a large pension pot. With no estate goal you could choose to draw the pensions harder (accepting
            20% tax above the allowance) if you'd rather run them down; the model takes the lowest-tax route by default. This
            is illustrative modelling, not regulated advice — the 25% tax-free pension lump sum isn't separately modelled (so
            real tax is a little lower than shown), and you should confirm any approach with a financial adviser before acting.
          </p>
        </div>
      </Card>
    </div>
  );
}
